import { Dispatch, FC, SetStateAction, useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { KTSVG } from '../../../../../../../../_metronic/helpers'
import { InputTextWrapper } from '../../../../../../components/UI/Form/Input'
import { getSnackBarToast } from '../../../../../../util/snackBar'
import { createCargoService, getListCargosService } from '../../../../services/booking.service'
import { updateCargoGuestService } from '../../../../services/guest.service'
import { Button } from '../../../../../../components/UI/Button'

interface Props {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  onCargoCreated: (cargoId?: string) => void
  huespedId?: string
}

const createCargoSchema = Yup.object().shape({
  nombre: Yup.string().required('Nombre del cargo es requerido'),
})

interface CargoItem {
  Id: string
  NombreCorto: string
  NombreLargo: string
  Codigo: string
}

const ModalCreateCargo: FC<Props> = ({ show, setShow, onCargoCreated, huespedId }) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [existingCargos, setExistingCargos] = useState<CargoItem[]>([])
  const [filteredCargos, setFilteredCargos] = useState<CargoItem[]>([])
  const [hasExactMatch, setHasExactMatch] = useState<boolean>(false)
  const [statusMessage, setStatusMessage] = useState<string>('')

  const methods = useForm<{ nombre: string }>({
    resolver: yupResolver(createCargoSchema),
    defaultValues: {
      nombre: '',
    },
  })

  const {
    handleSubmit,
    reset,
    control,
  } = methods

  const nombreValue = useWatch({
    control,
    name: 'nombre',
  })

  const normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
  }

  useEffect(() => {
    if (show) {
      getCargosList()
    } else {
      setFilteredCargos([])
      setHasExactMatch(false)
    }
  }, [show])

  useEffect(() => {
    if (!nombreValue || nombreValue.trim() === '') {
      setFilteredCargos([])
      setHasExactMatch(false)
      return
    }

    const normalizedInput = normalizeString(nombreValue)
    const matches: CargoItem[] = []
    let exactMatch = false

    existingCargos.forEach((cargo) => {
      const normalizedCorto = normalizeString(cargo.NombreCorto || '')
      const normalizedLargo = normalizeString(cargo.NombreLargo || '')

      if (
        normalizedCorto.includes(normalizedInput) ||
        normalizedLargo.includes(normalizedInput)
      ) {
        matches.push(cargo)

        if (
          normalizedCorto === normalizedInput ||
          normalizedLargo === normalizedInput
        ) {
          exactMatch = true
        }
      }
    })

    setFilteredCargos(matches)
    setHasExactMatch(exactMatch)
  }, [nombreValue, existingCargos])

  const getCargosList = async () => {
    try {
      const response = await getListCargosService()
      if (!response.data.error && response.data.data) {
        setExistingCargos(response.data.data)
      }
    } catch (error) {
      console.error('Error getting cargos list:', error)
      setExistingCargos([])
    }
  }

  const handleClose = () => {
    setShow(false)
    reset()
    setFilteredCargos([])
    setHasExactMatch(false)
    setStatusMessage('')
  }

  const onSubmit = async (data: { nombre: string }) => {
    try {
      setIsSubmitting(true)
      setStatusMessage('Se está creando...')

      // Paso 1: Crear el cargo
      const response = await createCargoService({ nombre: data.nombre })

      if (response.data.error) {
        getSnackBarToast(response.data.message, 'error')
        setIsSubmitting(false)
        setStatusMessage('')
        return
      }

      const cargoId = response.data.data?.Id || response.data.data?.id || response.data.data

      // Paso 2: Si hay huespedId, actualizar el huésped con el nuevo cargo
      if (huespedId && cargoId) {
        setStatusMessage('Ahora estamos asignándole...')

        try {
          const updateResponse = await updateCargoGuestService({
            Id: huespedId,
            CargoId: String(cargoId),
          })

          if (updateResponse.data.error) {
            getSnackBarToast(
              updateResponse.data.message || 'Error al asignar el cargo al huésped',
              'error',
            )
            setIsSubmitting(false)
            setStatusMessage('')
            return
          }

          // Éxito en ambos servicios
          getSnackBarToast(
            'Cargo creado y asignado al huésped con éxito. Ya puedes seleccionar el cargo.',
            'success',
          )
          onCargoCreated(String(cargoId))
          handleClose()
        } catch (error: any) {
          getSnackBarToast(
            error?.response?.data?.message || 'Error al asignar el cargo al huésped',
            'error',
          )
          setIsSubmitting(false)
          setStatusMessage('')
          return
        }
      } else {
        // Solo se creó el cargo, no hay huésped para actualizar
        getSnackBarToast('Cargo creado con éxito. Ya puedes seleccionar el cargo.', 'success')
        onCargoCreated(String(cargoId))
        handleClose()
      }
    } catch (error: any) {
      getSnackBarToast(error?.response?.data?.message || 'Error al crear cargo', 'error')
      setIsSubmitting(false)
      setStatusMessage('')
    }
  }

  return (
    <Modal
      className='modal fade'
      show={show}
      dialogClassName='modal-md'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Crear Cargo</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <FormProvider {...methods}>
            <form>
              <div className='row'>
                <div className='col-sm-12'>
                  <InputTextWrapper
                    labelText='Descripción'
                    name='nombre'
                    textPlaceholder='Ingrese el nombre del cargo'
                    type='any'
                    required
                  />
                  {hasExactMatch && (
                    <div className='text-danger mt-1 fs-7'>
                      Ya existe un cargo con este nombre (considerando tildes)
                    </div>
                  )}
                  {filteredCargos.length > 0 && (
                    <div className='mt-2'>
                      <label className='form-label fs-7 text-muted'>
                        Cargos similares encontrados:
                      </label>
                      <div className='border rounded p-2' style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {filteredCargos.map((cargo, index) => (
                          <div
                            key={cargo.Id}
                            className={`py-1 px-2 ${index % 2 === 0 ? 'bg-light' : 'bg-white'}`}
                          >
                            <span className='text-gray-700'>
                              {cargo.NombreCorto || cargo.NombreLargo}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
          {statusMessage && (
            <div className='alert alert-info d-flex align-items-center mt-4 border-0'>
              <div className='spinner-border spinner-border-sm me-2' role='status'>
                <span className='visually-hidden'>Cargando...</span>
              </div>
              <span>{statusMessage}</span>
            </div>
          )}
        </div>
        <div className='modal-footer'>
          <Button label='Cancelar' classNameProp='btn btn-light' onClick={handleClose} />
          <Button
            label={isSubmitting ? 'Guardando...' : 'Guardar'}
            classNameProp='btn btn-primary'
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || hasExactMatch}
          />
        </div>
      </div>
    </Modal>
  )
}

export { ModalCreateCargo }
